import React, { useState } from "react";
import {
	Alert,
	Box,
	Button,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Switch,
	TextField,
	Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useDispatch } from "react-redux";

import globalAxios from "../../globalAxios";
import { fetchPersonals } from "../../index.js";

import { useAppSelector } from "../../store/hooks.ts";
import {
	selectCreateDate,
	setCreateDate,
	setSelectedEntityType,
} from "../../store/slices/calendarSlice.ts";

import {
	addPersonal,
	clearSelectedPersonal,
	removePersonal,
	selectSelectedPersonal,
	updatePersonal,
} from "../../store/slices/personalsSlice.ts";

type PersonalViewMode = "create" | "view" | "edit";

type RecurrenceOption =
	| "none"
	| "daily"
	| "weekly"
	| "fortnightly"
	| "monthly"
	| "yearly";

const recurrenceOptions: {
	value: RecurrenceOption;
	label: string;
	rule: string | null;
}[] = [
	{ value: "none", label: "Does not repeat", rule: null },
	{ value: "daily", label: "Daily", rule: "FREQ=DAILY" },
	{ value: "weekly", label: "Weekly", rule: "FREQ=WEEKLY" },
	{
		value: "fortnightly",
		label: "Fortnightly",
		rule: "FREQ=WEEKLY;INTERVAL=2",
	},
	{ value: "monthly", label: "Monthly", rule: "FREQ=MONTHLY" },
	{ value: "yearly", label: "Yearly", rule: "FREQ=YEARLY" },
];

const recurrenceRuleToOption = (
	rule: string | null | undefined,
): RecurrenceOption => {
	if (!rule) return "none";

	const match = recurrenceOptions.find((option) => option.rule === rule);

	return match?.value ?? "none";
};

const recurrenceOptionToRule = (option: RecurrenceOption) => {
	return (
		recurrenceOptions.find((item) => item.value === option)?.rule ?? null
	);
};

const recurrenceOptionToLabel = (option: RecurrenceOption) => {
	return (
		recurrenceOptions.find((item) => item.value === option)?.label ??
		"Does not repeat"
	);
};

const ViewField = ({
	label,
	value,
}: {
	label: string;
	value?: string | null | boolean;
}) => {
	let displayValue: React.ReactNode = value;

	if (typeof value === "boolean") {
		displayValue = value ? "Yes" : "No";
	}

	if (value === null || value === undefined || value === "") {
		displayValue = "—";
	}

	return (
		<Box>
			<Typography
				variant="caption"
				color="text.secondary"
				sx={{
					fontWeight: 600,
					textTransform: "uppercase",
					letterSpacing: "0.04em",
				}}
			>
				{label}
			</Typography>

			<Typography variant="body1">{displayValue}</Typography>
		</Box>
	);
};

const PersonalView = () => {
	const dispatch = useDispatch();

	const createDate = useAppSelector(selectCreateDate);
	const selectedPersonal = useAppSelector(selectSelectedPersonal);

	const [mode, setMode] = useState<PersonalViewMode>(
		selectedPersonal ? "view" : "create",
	);

	const isViewMode = mode === "view";
	const isCreateMode = mode === "create";
	const isEditMode = mode === "edit";

	const [title, setTitle] = useState(selectedPersonal?.title || "");
	const [description, setDescription] = useState(
		selectedPersonal?.description || "",
	);

	const [startDate, setStartDate] = useState(
		selectedPersonal?.start_date || createDate || "",
	);

	const [endDate, setEndDate] = useState(
		selectedPersonal?.end_date || createDate || "",
	);

	const [startTime, setStartTime] = useState(
		selectedPersonal?.start_time || "",
	);

	const [endTime, setEndTime] = useState(selectedPersonal?.end_time || "");

	const [allDay, setAllDay] = useState(
		selectedPersonal ? Boolean(selectedPersonal.all_day) : true,
	);

	const [location, setLocation] = useState(selectedPersonal?.location || "");

	const [recurrenceOption, setRecurrenceOption] = useState<RecurrenceOption>(
		recurrenceRuleToOption(selectedPersonal?.recurrence_rule),
	);

	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [error, setError] = useState("");

	const resetNavigation = () => {
		dispatch(setSelectedEntityType(null));
		dispatch(setCreateDate(null));
		dispatch(clearSelectedPersonal());
	};

	const resetFormToSelected = () => {
		if (!selectedPersonal) return;

		setTitle(selectedPersonal.title || "");
		setDescription(selectedPersonal.description || "");
		setStartDate(selectedPersonal.start_date || "");
		setEndDate(selectedPersonal.end_date || "");
		setStartTime(selectedPersonal.start_time || "");
		setEndTime(selectedPersonal.end_time || "");
		setAllDay(Boolean(selectedPersonal.all_day));
		setLocation(selectedPersonal.location || "");
		setRecurrenceOption(
			recurrenceRuleToOption(selectedPersonal.recurrence_rule),
		);
	};

	const buildPayload = () => ({
		title,
		description: description || null,
		start_date: startDate,
		end_date: endDate || null,
		start_time: allDay ? null : startTime || null,
		end_time: allDay ? null : endTime || null,
		all_day: allDay,
		location: location || null,
		recurrence_rule: recurrenceOptionToRule(recurrenceOption),
	});

	const validate = () => {
		if (!title.trim()) return "Title is required.";
		if (!startDate) return "Start date is required.";

		if (!allDay && (!startTime || !endTime)) {
			return "Start and end time required for timed events.";
		}

		return "";
	};

	const handleSave = async () => {
		setError("");

		const validationError = validate();

		if (validationError) {
			setError(validationError);
			return;
		}

		try {
			setSaving(true);

			const payload = buildPayload();

			if (isEditMode && selectedPersonal) {
				const res = await globalAxios.put(
					`/personals/${selectedPersonal.id}`,
					payload,
				);

				dispatch(
					updatePersonal({
						id: selectedPersonal.id,
						updates: res.data,
					}),
				);
			}

			if (isCreateMode) {
				const res = await globalAxios.post("/personals", payload);

				dispatch(addPersonal(res.data));
			}

			await fetchPersonals(dispatch);

			resetNavigation();
		} catch (err) {
			console.error("[PersonalView] save failed:", err);
			setError("Failed to save personal event.");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!selectedPersonal) return;

		const confirmed = window.confirm("Delete this personal event?");

		if (!confirmed) return;

		try {
			setDeleting(true);

			await globalAxios.delete(`/personals/${selectedPersonal.id}`);

			dispatch(removePersonal(selectedPersonal.id));

			await fetchPersonals(dispatch);

			resetNavigation();
		} catch (err) {
			console.error("[PersonalView] delete failed:", err);
			setError("Failed to delete personal event.");
		} finally {
			setDeleting(false);
		}
	};

	const handleCancelEdit = () => {
		resetFormToSelected();
		setMode("view");
		setError("");
	};

	const handleRecurrenceChange = (e: SelectChangeEvent) => {
		setRecurrenceOption(e.target.value as RecurrenceOption);
	};

	return (
		<Box p={3}>
			<Stack spacing={2} maxWidth={520}>
				<Typography variant="h5">
					{isCreateMode
						? "Create Personal Event"
						: isEditMode
							? "Edit Personal Event"
							: "Personal Event"}
				</Typography>

				{error ? <Alert severity="error">{error}</Alert> : null}

				{isViewMode ? (
					<ViewField label="Title" value={title} />
				) : (
					<TextField
						label="Title"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						required
						size="small"
						fullWidth
					/>
				)}

				{isViewMode ? (
					<ViewField label="Description" value={description} />
				) : (
					<TextField
						label="Description"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						multiline
						rows={3}
						size="small"
						fullWidth
					/>
				)}

				<Divider />

				{isViewMode ? (
					<ViewField label="All Day Event" value={allDay} />
				) : (
					<FormControlLabel
						control={
							<Switch
								checked={allDay}
								onChange={(e) => setAllDay(e.target.checked)}
							/>
						}
						label="All Day Event"
					/>
				)}

				{isViewMode ? (
					<ViewField label="Start Date" value={startDate} />
				) : (
					<TextField
						label="Start Date"
						type="date"
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
						InputLabelProps={{
							shrink: true,
						}}
						size="small"
						fullWidth
					/>
				)}

				{isViewMode ? (
					<ViewField label="End Date" value={endDate} />
				) : (
					<TextField
						label="End Date"
						type="date"
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						InputLabelProps={{
							shrink: true,
						}}
						size="small"
						fullWidth
					/>
				)}

				{!allDay &&
					(isViewMode ? (
						<>
							<ViewField label="Start Time" value={startTime} />

							<ViewField label="End Time" value={endTime} />
						</>
					) : (
						<>
							<TextField
								label="Start Time"
								type="time"
								value={startTime}
								onChange={(e) => setStartTime(e.target.value)}
								InputLabelProps={{
									shrink: true,
								}}
								size="small"
								fullWidth
							/>

							<TextField
								label="End Time"
								type="time"
								value={endTime}
								onChange={(e) => setEndTime(e.target.value)}
								InputLabelProps={{
									shrink: true,
								}}
								size="small"
								fullWidth
							/>
						</>
					))}

				{isViewMode ? (
					<ViewField label="Location" value={location} />
				) : (
					<TextField
						label="Location"
						value={location}
						onChange={(e) => setLocation(e.target.value)}
						size="small"
						fullWidth
					/>
				)}

				{isViewMode ? (
					<ViewField
						label="Repeat"
						value={recurrenceOptionToLabel(recurrenceOption)}
					/>
				) : (
					<FormControl size="small" fullWidth>
						<InputLabel>Repeat</InputLabel>
						<Select
							value={recurrenceOption}
							label="Repeat"
							onChange={handleRecurrenceChange}
						>
							{recurrenceOptions.map((option) => (
								<MenuItem
									key={option.value}
									value={option.value}
								>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}

				<Divider />

				<Stack direction="row" spacing={1}>
					{isViewMode ? (
						<>
							<Button
								disableElevation
								variant="contained"
								onClick={() => setMode("edit")}
							>
								Edit
							</Button>

							<Button
								variant="outlined"
								onClick={resetNavigation}
							>
								Back
							</Button>

							<Button
								variant="outlined"
								color="error"
								onClick={handleDelete}
								disabled={deleting}
							>
								{deleting ? "Deleting..." : "Delete"}
							</Button>
						</>
					) : (
						<>
							<Button
								disableElevation
								variant="contained"
								onClick={handleSave}
								disabled={saving}
							>
								{saving
									? isEditMode
										? "Updating..."
										: "Saving..."
									: isEditMode
										? "Save Changes"
										: "Save"}
							</Button>

							<Button
								variant="outlined"
								onClick={
									isEditMode
										? handleCancelEdit
										: resetNavigation
								}
								disabled={saving}
							>
								Cancel
							</Button>
						</>
					)}
				</Stack>
			</Stack>
		</Box>
	);
};

export default PersonalView;
